import { useReadDashboardSharedState } from '@hopin-team/dashboard-shared-react';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
} from 'react';

import { ContactTypeAll } from '../features/top-bar/contact-type-filter';
import { EventTypes } from '../features/top-bar/event-type-filter';

const setCookie = (name: string, value: any, days = 1) => {
  const expires = new Date(
    Date.now() + days * 24 * 60 * 60 * 1000,
  ).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(
    JSON.stringify(value),
  )}; expires=${expires}; path=/`;
};

const getCookie = (name: string) => {
  const cookies = document.cookie.split('; ');
  const foundCookie = cookies.find(row => row.startsWith(name));
  return foundCookie
    ? JSON.parse(decodeURIComponent(foundCookie.split('=')[1]))
    : null;
};

export type AnalyticsState = {
  selectedIndex: number;
  chartSelectedIndex: number;
  selectedTab: string;
  searchText: string;
  dateRange: string;
  startDate: string | undefined;
  endDate: string | undefined;
  eventTypes: EventTypes;
  contactType: ContactTypeAll;
  eventSortName: string;
  eventSortDirection: string;
  registrantSortName: string;
  registrantSortDirection: string;
  videoSortName: string;
  videoSortDirection: string;
  selectedVideos: string[];
  selectedEvents: string[];
};

type Action =
  | {
      type: 'analytics tab updated';
      payload: { index: number; tabName: string };
    }
  | {
      type: 'analytics chart tab updated';
      payload: { index: number };
    }
  | { type: 'update search text'; payload: { searchText: string } }
  | {
      type: 'update date filter';
      payload: { dateRange: string; startDate?: string; endDate?: string };
    }
  | {
      type: 'update events sort key and direction';
      payload: { name: string; direction: string };
    }
  | {
      type: 'update registrants sort key and direction';
      payload: { name: string; direction: string };
    }
  | { type: 'update venue filter'; payload: { eventTypes: EventTypes } }
  | { type: 'update contact filter'; payload: { contactType: ContactTypeAll } }
  | {
      type: 'update videos sort key and direction';
      payload: { name: string; direction: string };
    }
  | {
      type: 'update selected videos';
      payload: { videos: string[] };
    }
  | {
      type: 'update selected events';
      payload: { events: string[] };
    };

type Dispatch = (action: Action) => void;

const AnalyticsStateContext = createContext<
  { state: AnalyticsState; dispatch: Dispatch } | undefined
>(undefined);

const initialState: AnalyticsState = {
  selectedIndex: 0,
  chartSelectedIndex: 0,
  selectedTab: 'overview',
  searchText: '',
  dateRange: 'last-year',
  startDate: '',
  endDate: '',
  eventTypes: { virtual: true, onsite: true, hybrid: true, webinar: true },
  contactType: 'all',
  eventSortName: '',
  eventSortDirection: '',
  registrantSortName: '',
  registrantSortDirection: '',
  videoSortName: '',
  videoSortDirection: '',
  selectedVideos: [],
  selectedEvents: [],
};

function analyticsStateReducer(
  state: AnalyticsState,
  action: Action,
): AnalyticsState {
  switch (action.type) {
    case 'analytics tab updated': {
      return {
        ...state,
        selectedIndex: action.payload.index,
        selectedTab: action.payload.tabName,
      };
    }
    case 'analytics chart tab updated': {
      return {
        ...state,
        chartSelectedIndex: action.payload.index,
      };
    }
    case 'update search text': {
      return {
        ...state,
        searchText: action.payload.searchText,
      };
    }
    case 'update date filter': {
      return {
        ...state,
        dateRange: action.payload.dateRange,
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
      };
    }
    case 'update venue filter': {
      return {
        ...state,
        eventTypes: action.payload.eventTypes,
      };
    }
    case 'update contact filter': {
      return {
        ...state,
        contactType: action.payload.contactType,
      };
    }
    case 'update events sort key and direction': {
      return {
        ...state,
        eventSortName: action.payload.name,
        eventSortDirection: action.payload.direction,
      };
    }
    case 'update registrants sort key and direction': {
      return {
        ...state,
        registrantSortName: action.payload.name,
        registrantSortDirection: action.payload.direction,
      };
    }
    case 'update videos sort key and direction': {
      return {
        ...state,
        videoSortName: action.payload.name,
        videoSortDirection: action.payload.direction,
      };
    }
    case 'update selected videos': {
      return {
        ...state,
        selectedVideos: action.payload.videos,
      };
    }
    case 'update selected events': {
      return {
        ...state,
        selectedEvents: action.payload.events,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${(action as Action).type}`);
    }
  }
}

type AnalyticsProviderProps = {
  children: ReactNode;
  initialTab?: string;
  persistFilters?: boolean;
};

export const AnalyticsProvider = ({
  children,
  initialTab,
  persistFilters = true,
}: AnalyticsProviderProps) => {
  const orgId: string = useReadDashboardSharedState('org_id') || '';

  const initialFilter = getCookie(`${orgId}_filter`) || {
    ...initialState,
    registrantSortName: 'engagementScore',
    registrantSortDirection: 'desc',
    videoSortName: 'times_watched',
    videoSortDirection: 'desc',
    dateRange: 'all-time',
  };

  const updatedState = {
    ...initialState,
    ...initialFilter,
    ...(initialTab !== undefined && { selectedTab: initialTab }),
  };
  const [state, dispatch] = useReducer(analyticsStateReducer, updatedState);

  useEffect(() => {
    if (!persistFilters) return;

    setCookie(`${orgId}_filter`, state);
  }, [orgId, state, persistFilters]);

  return (
    <AnalyticsStateContext.Provider value={{ state, dispatch }}>
      {children}
    </AnalyticsStateContext.Provider>
  );
};

export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsStateContext);
  if (!context) {
    throw new Error(
      'useAnalyticsContext must be used within a AnalyticsProvider',
    );
  }
  return context;
};
